import type { AppRecord, ListColumn } from '../types'

export const opportunityRevenueLevels = {
  estimated: { label: 'Manual estimate', revenue: 'estimatedValue', profit: null, margin: null },
  latest: { label: 'Latest quoted', revenue: 'latestQuotedRevenueNet', profit: 'latestQuotedGrossProfit', margin: 'latestQuotedGrossMarginPct' },
  active: { label: 'Active offer', revenue: 'activeOfferRevenueNet', profit: 'activeOfferGrossProfit', margin: 'activeOfferGrossMarginPct' },
  accepted: { label: 'Accepted', revenue: 'acceptedRevenueNet', profit: 'acceptedGrossProfit', margin: 'acceptedGrossMarginPct' },
} as const

export type OpportunityRevenueLevel = keyof typeof opportunityRevenueLevels
export const DEFAULT_OPPORTUNITY_REVENUE_LEVEL: OpportunityRevenueLevel = 'latest'

export const activeOpportunityStatuses = new Set(['new', 'visit_scheduled', 'measured', 'quote_in_preparation', 'quote_sent', 'negotiation'])

export function isOpportunityFinanciallyReportable(record: AppRecord) {
  const status = String(record.status || '').toLowerCase()
  return status === 'won' || activeOpportunityStatuses.has(status)
}

function finiteValue(value: unknown): number | null {
  if (value === null || value === undefined || (typeof value === 'string' && !value.trim())) return null
  const amount = typeof value === 'number' ? value : typeof value === 'string' ? Number(value.trim()) : NaN
  return Number.isFinite(amount) ? amount : null
}

function cents(value: number) { return Math.round((value + Number.EPSILON) * 100) }

export type OpportunityRevenueAggregate = {
  revenue: number | null
  profit: number | null
  marginPct: number | null
  opportunityCount: number
  missingProfitCount: number
}

export function columnsForOpportunityCurrentValue(columns: ListColumn[]): ListColumn[] {
  return columns.flatMap((column) => column.key === 'estimatedValue' ? [
    { key: 'reportRevenueNet', label: 'Revenue net', kind: 'money' },
    { key: 'reportGrossProfit', label: 'Direct-cost profit', kind: 'money' },
    { key: 'reportGrossMarginPct', label: 'Margin', kind: 'percent' },
    { key: 'reportValueSource', label: 'Value source', kind: 'text' },
  ] : [column])
}

export function resolveOpportunityRevenueLevel(record: AppRecord): Exclude<OpportunityRevenueLevel, 'estimated'> | null {
  for (const level of ['accepted', 'active', 'latest'] as const) {
    const revenueKey = opportunityRevenueLevels[level].revenue
    if (finiteValue(record[revenueKey]) !== null) return level
  }
  return null
}

export function withCurrentOpportunityRevenue(record: AppRecord): AppRecord {
  const level = resolveOpportunityRevenueLevel(record)
  const definition = level ? opportunityRevenueLevels[level] : null
  return {
    ...record,
    reportRevenueNet: definition ? finiteValue(record[definition.revenue]) : null,
    reportGrossProfit: definition?.profit ? finiteValue(record[definition.profit]) : null,
    reportGrossMarginPct: definition?.margin ? finiteValue(record[definition.margin]) : null,
    reportValueSource: level ? definition!.label : '—',
  }
}

export function aggregateOpportunityRevenue(records: AppRecord[], level: OpportunityRevenueLevel): OpportunityRevenueAggregate {
  const definition = opportunityRevenueLevels[level]
  const contributions = records.flatMap((record) => {
    const revenue = finiteValue(record[definition.revenue])
    return revenue === null ? [] : [{ revenueCents: cents(revenue), profitCents: definition.profit ? finiteValue(record[definition.profit]) : null }]
  })
  return aggregateContributions(contributions, Boolean(definition.profit))
}

export function aggregateCurrentOpportunityRevenue(records: AppRecord[]): OpportunityRevenueAggregate {
  const contributions = records.flatMap((record) => {
    const level = resolveOpportunityRevenueLevel(record)
    if (!level) return []
    const definition = opportunityRevenueLevels[level]
    const revenue = finiteValue(record[definition.revenue])
    return revenue === null ? [] : [{ revenueCents: cents(revenue), profitCents: definition.profit ? finiteValue(record[definition.profit]) : null }]
  })
  return aggregateContributions(contributions, true)
}

// The list uses a simple average of displayed, non-zero margins. Dashboard
// aggregates intentionally keep their existing revenue-weighted calculation.
export function summarizeOpportunityListRevenue(records: AppRecord[]): OpportunityRevenueAggregate & { marginSampleCount: number } {
  const reportable = records.filter(isOpportunityFinanciallyReportable)
  const totals = aggregateCurrentOpportunityRevenue(reportable)
  const margins = reportable.flatMap((record) => {
    const margin = finiteValue(withCurrentOpportunityRevenue(record).reportGrossMarginPct)
    return margin === null || margin === 0 ? [] : [margin]
  })
  return {
    ...totals,
    marginPct: margins.length ? Math.round((margins.reduce((sum, margin) => sum + margin, 0) / margins.length + Number.EPSILON) * 100) / 100 : null,
    marginSampleCount: margins.length,
  }
}

function aggregateContributions(contributions: Array<{ revenueCents: number; profitCents: number | null }>, hasProfitLevel: boolean): OpportunityRevenueAggregate {
  if (!contributions.length) return { revenue: null, profit: null, marginPct: null, opportunityCount: 0, missingProfitCount: 0 }
  const revenueCents = contributions.reduce((sum, row) => sum + row.revenueCents, 0)
  const missingProfitCount = hasProfitLevel ? contributions.filter((row) => row.profitCents === null).length : contributions.length
  const profitCents = hasProfitLevel && missingProfitCount === 0
    ? contributions.reduce((sum, row) => sum + cents(row.profitCents!), 0)
    : null
  const marginPct = profitCents !== null && revenueCents !== 0
    ? Math.round((profitCents / revenueCents * 100) * 100) / 100
    : null
  return { revenue: revenueCents / 100, profit: profitCents === null ? null : profitCents / 100, marginPct, opportunityCount: contributions.length, missingProfitCount }
}
